import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, useNavigate, Outlet, useOutletContext } from 'react-router-dom';

import { 
  Package2, 
  Users, 
  FileText, 
  FileBarChart2, 
  Building2, 
  ChevronDown, 
  Plus, 
  Menu, 
  DollarSign,
  Briefcase,
  Activity,
  Wrench,
  Globe,
  CreditCard,
  LogOut,
  User,
  CheckSquare,
  Settings2,
  Clock,
  Megaphone,
  GlobeIcon,
  MapPin,
  BarChart,
  TrendingUp,
  LineChart,
  MessageSquare,
  ListChecks
} from 'lucide-react';
import Services from './pages/Services';
import Customers from './pages/Customers';
import Reports from './pages/Reports';
import Stock from './pages/Stock';
import Proposals from './pages/Proposals';
import AIInsights from './pages/AIInsights';
import Resources from './pages/Resources';
import MobileSettings from './pages/settings/MobileSettings';
import Payments from './pages/Payments';
import Workflows from './pages/Workflows';
import Bookings from './pages/Bookings';
import ManagePayroll from './pages/ManagePayroll';
import Settings from './pages/Settings';
import Marketing from './pages/Marketing';
import FlatRates from './pages/settings/FlatRates';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ResetPassword from './pages/auth/ResetPassword';
import UpdatePassword from './pages/auth/UpdatePassword';
import TeamReports from './pages/TeamReports';
import GPS from './pages/GPS';
import PrivateRoute from './components/PrivateRoute';
import { supabase } from './lib/supabase';
import { BusinessProvider, useBusiness } from './contexts/BusinessContext';
import { UserProvider } from './contexts/UserContext';
import Profile from './pages/Profile';
import { getResourceByAuthId } from './lib/resources';
import { setupInspectionPhotosBucket } from './lib/storage';
import { SupabaseProvider } from './contexts/SupabaseContext';
import { MouseEvent as DOMMouseEvent } from 'react';
import SalesReports from './pages/SalesReports';
import Sales from './pages/Sales';
import ProfitLoss from './pages/ProfitLoss';
import LeftNavigation from './components/LeftNavigation';
import PublicReceipt from './pages/PublicReceipt';
import PublicProposal from './pages/PublicProposal';
import Tasks from './pages/tasks/Tasks';
import TasksDropdown from './components/tasks/TasksDropdown';
import { getTasks, updateTaskStatus, Task } from './lib/tasks';
import FloatingActionButton from './components/FloatingActionButton';
import MobileBottomNavigation from './components/MobileBottomNavigation';
import Timeclock from './pages/Timeclock';

import { NotificationsProvider } from './contexts/NotificationsContext';
import { TasksProvider, useTasks } from './contexts/TasksContext';
import { UnreadTasksProvider, useUnreadTasks } from './contexts/UnreadTasksContext';
import UnreadTasksBadge from './components/UnreadTasksBadge';
import CashFlow from './pages/CashFlow';
import BookingStatus from './contexts/BookingStatusContext';
import BookOnline from './pages/public/BookOnline';
import BookingSuccess from './pages/public/BookingSuccess';
import Messages from "./pages/Messages";
import Automations from "./pages/Automations";
import AutomationsV2 from "./pages/AutomationsV2";
import OnlineBookings from './pages/settings/OnlineBookings';
import { ToastProvider } from './components/toasts/SimpleToast';
import CreateTaskModal from './components/tasks/CreateTaskModal';
import CreateBookingModal from './components/CreateBookingModal';
import CreateProposalModal from './components/CreateProposalModal';
import WithdrawStockModal from './components/WithdrawStockModal';
import PaymentModal from './components/PaymentModal';
import { migrateLocalStorageToDatabase, runUserPreferencesMigration } from './utils/migrations';
import { useReminderProcessor } from './hooks/useReminderProcessor';

import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import { TwilioMessagePollerProvider } from './contexts/GlobalMessagePollerContext';
import { CallProvider } from './contexts/CallContext';
import { ApplicationSettingsProvider, useApplicationSettingsContext } from './contexts/ApplicationSettingsContext';
import { useSelectInputOnFocus } from './lib/utils';
import { PageId } from './hooks/useApplicationSettings';
import CalendarV2 from './pages/CalendarV2';
import MobileCalendar from './pages/MobileCalendar';
import Billing from './pages/Billing';
import BillingSuccess from './pages/BillingSuccess';
import TermsOfService from './pages/TermsOfService';
import SuppliesUsage from './pages/SuppliesUsage';
import CRM from './pages/CRM';
import CreateMobileBookingModal from './components/CreateMobileBookingModal';
import TestNotification from './pages/TestNotification';
import ToastContainer from './components/ToastContainer';
import Dialler from './pages/Dialler';
import FeedbackPage from './pages/FeedbackPage';
import BookingInfoModal from './components/BookingInfoModal';
import { useGenericModalPersistence, useEventDrivenModalPersistence } from './utils/modalPersistence';
import KPIDashboard from './pages/KPIDashboard';
import { PermissionProvider } from './contexts/PermissionContext';
import PermissionGuard from './components/PermissionGuard';
import { usePermissionCheck } from './hooks/usePermissionCheck';

// Development flag to control feature visibility
const isDevelopment = process.env.NODE_ENV === "development";

interface AppContentProps {
  // Remove task-related props
}

interface MenuItem {
  to: string;
  icon: React.ReactNode;
  text: string;
  permission?: string;
  onClick?: () => void;
}

const menuCategories = [
  {
    name: "Manage",
    items: [
      {
        to: "/timeclock",
        icon: <Clock className="h-4 w-4" />,
        text: "Timeclock",
        permission: "timeclock",
      },
      {
        to: "/payroll",
        icon: <DollarSign className="h-4 w-4" />,
        text: "Payroll",
        permission: "payroll",
      },
      {
        to: "/stock",
        icon: <Package2 className="h-4 w-4" />,
        text: "Supplies",
        permission: "stock",
      },
      {
        to: "/resources",
        icon: <Users className="h-4 w-4" />,
        text: "Resources",
        permission: "resources",
      },
      {
        to: "/services",
        icon: <Wrench className="h-4 w-4" />,
        text: "Services",
        permission: "services",
      },
    ] as MenuItem[],
  },
  {
    name: "Reports",
    items: [
      {
        to: "/sales-reports",
        icon: <BarChart className="h-4 w-4" />,
        text: "Sales Reports",
        permission: "sales_reports",
      },
    ] as MenuItem[],
  },
];

interface TopNavLinkProps {
  to: string;
  children: React.ReactNode;
}

function TopNavLink({ to, children }: TopNavLinkProps) {
  const location = useLocation();
  const isActive = location.pathname === to;
  const isAIInsights = to === "/insights";

  return (
    <Link
      to={to}
      className={`text-[13px] text-gray-300 hover:text-white relative py-4 ${
        isActive ? "text-white" : ""
      } ${isAIInsights ? "ai-insights-button px-3 py-1.5 rounded-md" : ""}`}
    >
      {children}
      {isActive && !isAIInsights && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white" />
      )}
    </Link>
  );
}

function AppContent() {
  // Initialize sidebar state based on viewport size
  const initialIsMobile = window.innerWidth < 768;
  const [sidebarOpen, setSidebarOpen] = useState(!initialIsMobile);
  const appContentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  const [hasBookings, setHasBookings] = useState(false);
  const [hasCalendar, setHasCalendar] = useState(false);

  const [proposalsAccess, setProposalsAccess] = useState<
    "manage" | "view" | "disabled"
  >("disabled");
  const [hasPayments, setHasPayments] = useState(false);
  const [hasCustomers, setHasCustomers] = useState(false);
  const [hasPayroll, setHasPayroll] = useState(false);

  const [hasSettings, setHasSettings] = useState(false);



  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [bottomNavItems, setBottomNavItems] = useState<string[]>([]);

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const [userInfo, setUserInfo] = useState<{ avatarUrl?: string } | null>(null);

  const [loading, setLoading] = useState(true);
  const [isTasksDropdownOpen, setIsTasksDropdownOpen] = useState(false);
  const tasksRef = useRef<HTMLDivElement>(null);
  const [bookingsPermissionLevel, setBookingsPermissionLevel] = useState<
    "manage" | "view" | "disabled"
  >("disabled");

  const [timeclockPermissionLevel, setTimeclockPermissionLevel] = useState<
    "manage" | "view" | "disabled"
  >("disabled");

  const [settingsPermissionLevel, setSettingsPermissionLevel] = useState<
    "manage" | "view" | "disabled"
  >("disabled");
  const { loadTasks } = useTasks();
  const { unreadCount } = useUnreadTasks();
  const [hasMessages, setHasMessages] = useState(false);
  // Get the application settings context to check for disabled pages
  const { isPageDisabled } = useApplicationSettingsContext();
  
  // Get permission checking functionality
  const { canAccess, isLoading: permissionsLoading } = usePermissionCheck();
  
  // FAB-related modal states - moved up for access by MobileBottomNavigation
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);
  const [resources, setResources] = useState<any[]>([]);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  
  // Helper function to check if a page should be shown based on permissions and disabled status
  const shouldShowPage = (pageId: PageId, hasPermission: boolean) => {
    return hasPermission && !isPageDisabled(pageId);
  };

  // PWA detection effect
  useEffect(() => {
    // Method 1: Check for display-mode: standalone
    const isDisplayModeStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    // Method 2: Check for iOS PWA (using window.navigator)
    const isIOSPWA = 
      // @ts-ignore - 'standalone' exists on iOS Safari but not in the TypeScript definition
      (window.navigator as any).standalone || 
      window.matchMedia('(display-mode: standalone)').matches;
    
    // Method 3: Check for PWA launch parameters
    const urlParams = new URLSearchParams(window.location.search);
    const launchedAsPWA = urlParams.get('pwa') === 'true';
    
    // Method 4: Check for iOS specific user agent
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    
    // Force PWA mode for testing if needed
    // const isPWALocal = true; // Uncomment for testing
    const isPWALocal = isDisplayModeStandalone || isIOSPWA || launchedAsPWA;
    
    // Update state
    setIsPWA(isPWALocal);
    
    // Add PWA class to body if in PWA mode
    if (isPWALocal) {
      document.body.classList.add('pwa-mode');
      // Set URL parameter for any page loads
      if (!launchedAsPWA && window.history.pushState) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('pwa', 'true');
        window.history.pushState({path: newUrl.toString()}, '', newUrl.toString());
      }
      
      // Apply iOS-specific classes if needed
      if (isIOS) {
        document.body.classList.add('ios-pwa-mode');
        // Fix for iOS bottom nav positioning
        const viewportMeta = document.querySelector('meta[name="viewport"]');
        if (viewportMeta) {
          viewportMeta.setAttribute('content', 
            'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no');
        } else {
          // Create viewport meta if it doesn't exist
          const meta = document.createElement('meta');
          meta.name = 'viewport';
          meta.content = 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no';
          document.head.appendChild(meta);
        }
      }
      
      console.log('Running in PWA mode', { isIOS, isDisplayModeStandalone, isIOSPWA });
    } else {
      document.body.classList.remove('pwa-mode');
      document.body.classList.remove('ios-pwa-mode');
      console.log('Running in browser mode');
    }
  }, []);

  // Add effect to prevent overscroll/bounce behavior on mobile
  useEffect(() => {
    // Function to prevent default on touchmove for certain elements
    const preventBounce = (event: TouchEvent) => {
      // Check if we're at the top of the page and trying to pull down
      if (window.scrollY === 0 && event.touches[0].clientY > 0) {
        event.preventDefault();
      }
    };

    // Apply styles to prevent elastic overscroll on iOS
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
    
    // Add event listeners to prevent bouncing on header
    const header = document.querySelector('header');
    if (header) {
      header.addEventListener('touchmove', preventBounce, { passive: false });
    }

    // Cleanup function
    return () => {
      document.body.style.overscrollBehavior = '';
      document.documentElement.style.overscrollBehavior = '';
      if (header) {
        header.removeEventListener('touchmove', preventBounce);
      }
    };
  }, []);

  useEffect(() => {
    const checkAccess = async () => {
      console.log("App: Checking permissions...");
      // All access levels now hardcoded - no longer using permissions
      const calendarAccess = true;
      const proposalsAccess = 'manage' as 'manage' | 'view' | 'disabled';
      const paymentsAccess = true;
      const payrollAccess = true;
      const tasksAccess = true;
      const timeclockAccess = 'manage' as 'manage' | 'view' | 'disabled';
      const messagesAccess = true;
      const automationsAccess = 'manage' as 'manage' | 'view' | 'disabled';
      console.log("App: Messages access:", messagesAccess);
      console.log("App: Automations access:", automationsAccess);
      setHasBookings(true);
      setHasCalendar(calendarAccess);
      setProposalsAccess(proposalsAccess);
      setHasPayments(paymentsAccess);
      setHasCustomers(true);
      setHasPayroll(payrollAccess);
      setHasSettings(true);
      setTimeclockPermissionLevel('manage');
      setHasMessages(true);
    };
    checkAccess();
  }, []);

  useEffect(() => {
    const loadUserInfo = async () => {
      if (!supabase) return;
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const resource = await getResourceByAuthId(user.id);
        if (resource) {
          setUserInfo({ avatarUrl: resource.avatar_url });
        }
      }
    };
    loadUserInfo();
  }, []);

  useEffect(() => {
    async function checkPermissions() {
      setLoading(false);
    }
    checkPermissions();
  }, []);

  useEffect(() => {
    const loadPermissions = async () => {
      setBookingsPermissionLevel('manage');
      setHasSettings(true);
      setSettingsPermissionLevel('manage');
    };
    loadPermissions();
  }, []);

  const handleSignOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  };

  useEffect(() => {
    function handleClickOutside(event: Event) {
      const target = event.target as Node;
      if (tasksRef.current && !tasksRef.current.contains(target)) {
        setIsTasksDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setShowMobileMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle closing the sidebar when clicking on a link (mobile only)
  useEffect(() => {
    if (appContentRef.current) {
      const handleCloseSidebar = () => {
        if (isMobile) {
          setSidebarOpen(false);
        }
      };
      
      appContentRef.current.addEventListener('closeSidebar', handleCloseSidebar);
      return () => {
        appContentRef.current?.removeEventListener('closeSidebar', handleCloseSidebar);
      };
    }
  }, [isMobile, appContentRef]);

  // Toggle body scroll when sidebar is open on mobile
  useEffect(() => {
    // Only handle scroll locking on mobile
    if (!isMobile) return;
    
    const html = document.documentElement;
    const body = document.body;
    
    if (sidebarOpen) {
      // Store current scroll position as a data attribute
      html.style.setProperty('--scroll-position', `${window.scrollY}px`);
      
      // Apply scroll lock to both html and body
      html.classList.add('scroll-locked');
      body.classList.add('scroll-locked');
      body.classList.add('sidebar-open');
      
      // Set the top offset to maintain scroll position
      body.style.top = `-${window.scrollY}px`;
    } else {
      // Get stored scroll position
      const scrollY = parseFloat(html.style.getPropertyValue('--scroll-position') || '0');
      
      // Remove all scroll lock classes
      html.classList.remove('scroll-locked');
      body.classList.remove('scroll-locked');
      body.classList.remove('sidebar-open');
      
      // Reset styles and restore scroll position
      body.style.top = '';
      window.scrollTo(0, scrollY);
    }
    
    return () => {
      // Cleanup: ensure all classes and styles are removed
      html.classList.remove('scroll-locked');
      body.classList.remove('scroll-locked');
      body.classList.remove('sidebar-open');
      body.style.top = '';
    };
  }, [sidebarOpen, isMobile]);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setShowMobileMenu(false);
    }
  }, [location.pathname, isMobile]);

  // Update isMobile state when window size changes
  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth < 768;
      setIsMobile(mobileView);
      
      // Only auto-open sidebar on desktop
      if (!mobileView) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Initialize app by running necessary setup functions
  useEffect(() => {
    const initializeApp = async () => {
      // Setup inspection photos bucket
      setupInspectionPhotosBucket();
    };
    
    initializeApp();
  }, []);

  const handleTasksDropdownOpen = () => {
    setIsTasksDropdownOpen(true);
  };

  // Add a useEffect to fetch resources when the task modal is opened
  useEffect(() => {
    if (isCreateTaskModalOpen && selectedBusiness?.id) {
      const fetchResources = async () => {
        try {
          const { getResources } = await import('./lib/resources');
          const fetchedResources = await getResources(selectedBusiness.id);
          setResources(fetchedResources);
        } catch (error) {
          console.error('Error fetching resources:', error);
        }
      };
      fetchResources();
    }
  }, [isCreateTaskModalOpen, selectedBusiness?.id]);

  // Event-driven modal persistence for CreateBookingModal
  const { hasModalData, clearModalData } = useEventDrivenModalPersistence('create_booking');
  
  // Event-driven modal persistence for CreateMobileBookingModal
  const { hasModalData: hasMobileModalData, clearModalData: clearMobileModalData } = useEventDrivenModalPersistence('create_mobile_booking');
  
  // Check for saved modal data on app load and auto-open if found
  useEffect(() => {
    const checkSavedModalData = () => {
      // Don't reopen if modal is already open
      if (isCreateBookingModalOpen) return;
      
      const hasData = hasModalData();
      console.log('🔍 Checking for saved CreateBookingModal data on App load:', hasData);
      if (hasData) {
        console.log('📥 Found saved CreateBookingModal progress - reopening modal (App.tsx)');
        setIsCreateBookingModalOpen(true);
      }
    };
    
    // Check immediately first
    checkSavedModalData();
    
    // Also check with multiple delays to handle different initialization timings
    const timer1 = setTimeout(checkSavedModalData, 100);
    const timer2 = setTimeout(checkSavedModalData, 500);
    const timer3 = setTimeout(checkSavedModalData, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [hasModalData, isCreateBookingModalOpen]);

  // Check for saved mobile modal data on app load and auto-open if found
  useEffect(() => {
    const checkSavedMobileModalData = () => {
      // Don't reopen if modal is already open
      if (isCreateMobileBookingModalOpen) return;
      
      const hasMobileData = hasMobileModalData();
      console.log('🔍 Checking for saved CreateMobileBookingModal data on App load:', hasMobileData);
      if (hasMobileData) {
        console.log('📥 Found saved CreateMobileBookingModal progress - reopening modal (App.tsx)');
        setIsCreateMobileBookingModalOpen(true);
      }
    };
    
    // Check immediately first
    checkSavedMobileModalData();
    
    // Also check with multiple delays to handle different initialization timings
    const timer1 = setTimeout(checkSavedMobileModalData, 100);
    const timer2 = setTimeout(checkSavedMobileModalData, 500);
    const timer3 = setTimeout(checkSavedMobileModalData, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [hasMobileModalData, isCreateMobileBookingModalOpen]);

  // PWA AUTO-REOPEN: Listen for when user returns to PWA after switching apps
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Only handle when app becomes visible again
      if (document.visibilityState === 'visible') {
        console.log('👁️ PWA became visible - checking for saved modal data to auto-reopen');
        
        // Check CreateBookingModal
        if (!isCreateBookingModalOpen && hasModalData()) {
          console.log('📱 PWA returned: Found saved CreateBookingModal progress - auto-reopening');
          setIsCreateBookingModalOpen(true);
        }
        
        // Check CreateMobileBookingModal  
        if (!isCreateMobileBookingModalOpen && hasMobileModalData()) {
          console.log('📱 PWA returned: Found saved CreateMobileBookingModal progress - auto-reopening');
          setIsCreateMobileBookingModalOpen(true);
        }
      }
    };

    const handleFocus = () => {
      // Additional handler for window focus (backup for visibility change)
      console.log('🎯 PWA gained focus - checking for saved modal data to auto-reopen');
      
      // Small delay to ensure any pending state updates are complete
      setTimeout(() => {
        // Check CreateBookingModal
        if (!isCreateBookingModalOpen && hasModalData()) {
          console.log('📱 PWA focus: Found saved CreateBookingModal progress - auto-reopening');
          setIsCreateBookingModalOpen(true);
        }
        
        // Check CreateMobileBookingModal
        if (!isCreateMobileBookingModalOpen && hasMobileModalData()) {
          console.log('📱 PWA focus: Found saved CreateMobileBookingModal progress - auto-reopening');
          setIsCreateMobileBookingModalOpen(true);
        }
      }, 100);
    };

    // Add event listeners for PWA return detection
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    // PWA-specific events (some browsers support these)
    window.addEventListener('pageshow', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handleFocus);
    };
  }, [hasModalData, hasMobileModalData, isCreateBookingModalOpen, isCreateMobileBookingModalOpen]);

  if (businessLoading) {
    return (
      <div className="min-h-screen bg-[#f6f6f7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#f6f6f7] relative overflow-x-hidden"
      ref={appContentRef}
      data-app-content
    >
      <header className="main-header bg-black border-b border-[#1a1a1a] px-4 h-12 flex items-center justify-between fixed top-0 left-0 right-0 w-full z-[80]" style={{ touchAction: 'none', WebkitOverflowScrolling: 'auto' }}>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-gray-300"
            aria-label="Toggle sidebar menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Top Navigation - Only visible on desktop */}
          <nav className="hidden md:flex items-center space-x-6">
            {canAccess.kpiDashboard && (
              <TopNavLink to="/kpi-dashboard">
                <span className="flex items-center space-x-1">
                  <span>KPI Dashboard</span>
                  <span className="inline-flex items-center px-0.5 py-0 rounded text-[9px] font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    BETA
                  </span>
                </span>
              </TopNavLink>
            )}
            {canAccess.calendar && shouldShowPage('calendarV2', hasCalendar) && <TopNavLink to="/calendarV2">Location Calendar</TopNavLink>}
            {canAccess.calendar && shouldShowPage('mobile-calendar', hasCalendar) && <TopNavLink to="/mobile-calendar">Mobile Calendar</TopNavLink>}
            {shouldShowPage('bookings', hasBookings) && canAccess.bookings && <TopNavLink to="/bookings">Bookings</TopNavLink>}
            {shouldShowPage('proposals', proposalsAccess !== "disabled") && canAccess.proposals && (
              <TopNavLink to="/proposals">Proposals</TopNavLink>
            )}
            {/* Payments page disabled temporarily - keeping code for future use */}
            {/* {shouldShowPage('payments', hasPayments) && <TopNavLink to="/payments">Payments</TopNavLink>} */}
            {shouldShowPage('customers', hasCustomers) && canAccess.customers && <TopNavLink to="/customers">Customers</TopNavLink>}
            {/* GPS link temporarily hidden */}
            {false && <TopNavLink to="/gps">GPS</TopNavLink>}
          </nav>

          {/* Mobile Navigation - REMOVE, now handled by LeftNavigation */}
          {/* <div className="md:hidden">
            <div className="relative" ref={mobileMenuRef}>
              <div>
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="text-gray-300 hover:text-white flex items-center space-x-1.5 px-1.5 py-1.5"
                >
                  <span className="text-sm font-medium">
                      ? "Calendar"
                      : location.pathname === "/bookings"
                      ? "Bookings"
                      : location.pathname === "/proposals"
                      ? "Proposals"
                      : location.pathname === "/payments"
                      ? "Payments"
                      : location.pathname === "/customers"
                      ? "Customers"
                      : location.pathname === "/gps"
                      ? "GPS"
                      : "Menu"}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${
                      showMobileMenu ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {showMobileMenu && (
                  <div className="mobile-menu absolute top-full left-1/2 -translate-x-1/2 w-32 transform transition-all duration-200 ease-out bg-white rounded-lg shadow-lg border border-gray-200">
                    {hasCalendar && (
                      <Link
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Calendar
                      </Link>
                    )}
                    {hasBookings && (
                      <Link
                        to="/bookings"
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Bookings
                      </Link>
                    )}
                    {proposalsAccess !== "disabled" && (
                      <Link
                        to="/proposals"
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Proposals
                      </Link>
                    )}
                    {hasPayments && (
                      <Link
                        to="/payments"
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Payments
                      </Link>
                    )}
                    {hasCustomers && (
                      <Link
                        to="/customers"
                        className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors border-b border-gray-100"
                        onClick={() => setShowMobileMenu(false)}
                      >
                        Customers
                      </Link>
                    )}
                    <Link
                      to="/gps"
                      className="mobile-menu-item w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setShowMobileMenu(false)}
                    >
                      GPS
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div> */}
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative" ref={tasksRef}>
            <style jsx>{`
              @keyframes danceAround {
                0% {
                  transform: translate(0, 0) rotate(0deg);
                }
                25% {
                  transform: translate(-2px, -2px) rotate(-5deg);
                }
                50% {
                  transform: translate(0, -3px) rotate(0deg);
                }
                75% {
                  transform: translate(2px, -2px) rotate(5deg);
                }
                100% {
                  transform: translate(0, 0) rotate(0deg);
                }
              }
              .tasks-button-container {
                display: inline-block;
              }
              .tasks-button-container.has-notifications {
                animation: danceAround 1.5s ease-in-out infinite;
              }
            `}</style>
            <div
              className={`tasks-button-container ${
                unreadCount > 0 && !isTasksDropdownOpen
                  ? "has-notifications"
                  : ""
              }`}
            >
              <button
                onClick={() =>
                  isTasksDropdownOpen
                    ? setIsTasksDropdownOpen(false)
                    : handleTasksDropdownOpen()
                }
                className="text-gray-400 hover:text-gray-300 flex flex-col items-center gap-0 relative pt-1"
              >
                <div className="relative">
                  <ListChecks className="h-4 w-4 relative z-10" />
                  <UnreadTasksBadge />
                </div>
                <ChevronDown
                  className={`h-3 w-3 transition-transform duration-200 ${
                    isTasksDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>
            <TasksDropdown
              isOpen={isTasksDropdownOpen}
              onClose={() => setIsTasksDropdownOpen(false)}
            />
          </div>
          
          {/* Sign Out Button */}
          <button
            onClick={handleSignOut}
            className="flex items-center space-x-2 text-gray-400 hover:text-gray-300 transition-colors duration-200"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
            <span className="text-sm font-medium hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      <div className="flex h-[calc(100vh-3rem)] mt-12">
        {/* Backdrop overlay for mobile - only show for non-PWA mode since PWA uses CSS backdrop */}
        {sidebarOpen && isMobile && !document.body.classList.contains('pwa-mode') && (
          <div
            className="fixed inset-0 bg-transparent z-[75] transition-opacity duration-300 ease-in-out"
            onClick={() => setSidebarOpen(false)}
            onTouchMove={(e) => e.preventDefault()}
            onWheel={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            style={{ 
              touchAction: 'none',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              pointerEvents: 'auto',
              overscrollBehavior: 'none',
              left: '280px', // Updated to match PWA sidebar width
              width: 'calc(100% - 280px)' // Only cover the content area, not the sidebar
            }}
            aria-hidden="true"
          />
        )}

        {/* PWA mode backdrop overlay - covers entire screen for click-outside functionality */}
        {sidebarOpen && isMobile && document.body.classList.contains('pwa-mode') && (
          <div
            className="fixed inset-0 z-[39]" // Just below sidebar z-index (40) but covers full screen
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSidebarOpen(false);
            }}
            onTouchMove={(e) => e.preventDefault()}
            onWheel={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
            style={{ 
              pointerEvents: 'auto',
              touchAction: 'none',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              overscrollBehavior: 'none',
              backgroundColor: 'transparent'
            }}
            aria-hidden="true"
          />
        )}

        {/* Sidebar - Update z-index and height, no need for extra padding since we removed the bottom nav */}
        <div
          className={`fixed ${
            sidebarOpen ? "w-64 md:w-48" : "w-0"
          } h-[calc(100vh-3rem)] bg-[#eeeeef] border-r border-[#e1e3e5] transition-all duration-300 z-[80] overflow-y-auto md:flex md:flex-col md:min-h-full ${
            sidebarOpen && isMobile ? "overflow-y-auto" : "overflow-hidden"
          }`}
          style={{
            top: '3rem', // Position below the header
            ...(isMobile ? {
              height: 'calc(100vh - 3rem)',
              maxHeight: 'calc(100vh - 3rem)',
              bottom: '0',
              paddingBottom: '20px', // Reduced padding since no bottom nav
              zIndex: 80,
              pointerEvents: 'auto'
            } : {})
          }}
        >
          <LeftNavigation
            hasSettings={hasSettings}
            isDevelopment={isDevelopment}
            timeclockPermissionLevel={timeclockPermissionLevel}
            hasCalendar={hasCalendar}
            hasBookings={hasBookings}
            proposalsAccess={proposalsAccess}
            hasPayments={hasPayments}
            hasCustomers={hasCustomers}
            hasMessages={hasMessages}
            isMobile={isMobile}
            bottomNavItems={bottomNavItems}
            sidebarOpen={sidebarOpen}
          />
        </div>

        {/* Main content area - adjust padding at the bottom, no longer need extra for the bottom nav */}
        <div className={`flex-1 transition-all duration-300 opacity-100`}
          style={{
            pointerEvents: isMobile && sidebarOpen ? 'none' : 'auto',
            width: sidebarOpen && !isMobile ? 'calc(100% - 192px)' : '100%', // Reduce width by sidebar width on desktop (w-48 = 192px)
            marginLeft: sidebarOpen && !isMobile ? '192px' : '0', // Add margin to align with sidebar on desktop (w-48 = 192px)
            transition: 'width 0.3s, margin-left 0.3s' // Smooth transition for width changes
          }}
        >
          <main className={`main-content bg-[#f6f6f7] p-4 md:p-6 ${isMobile ? 'pb-4' : ''}`}>
            <Outlet context={{ tasksPermissionLevel: "manage" as const, automationsPermissionLevel: "manage" as const, settingsPermissionLevel }} />
          </main>
        </div>
      </div>

      {/* Mobile Bottom Navigation - only visible on mobile, but not PWA */}
      {isMobile && !isPWA && (
        <MobileBottomNavigation
          hasCalendar={hasCalendar}
          hasBookings={hasBookings}
          proposalsAccess={proposalsAccess}
          hasPayments={hasPayments}
          hasCustomers={hasCustomers}
          permissionLevel={bookingsPermissionLevel}
          onSuccess={loadTasks}
          openCreateBookingModal={() => setIsCreateBookingModalOpen(true)}
          openCreateProposalModal={() => setIsCreateProposalModalOpen(true)}
          openPaymentModal={() => setIsPaymentModalOpen(true)}
          openCreateTaskModal={() => setIsCreateTaskModalOpen(true)}
          openWithdrawStockModal={() => setIsWithdrawStockModalOpen(true)}
          hasSettings={hasSettings}
          hasMessages={hasMessages}
          tasksPermissionLevel={"manage"}
          onNavItemsChange={setBottomNavItems}
          openCreateMobileBookingModal={() => setIsCreateMobileBookingModalOpen(true)}
        />
      )}

      {/* Show FloatingActionButton on all platforms - unified experience */}
      <FloatingActionButton
        permissionLevel={bookingsPermissionLevel}
        onSuccess={loadTasks}
      />
      
      {/* Modals - moved outside of FloatingActionButton */}
      {isCreateTaskModalOpen && (
        <CreateTaskModal
          isOpen={isCreateTaskModalOpen}
          onClose={() => setIsCreateTaskModalOpen(false)}
          resources={resources}
          onSuccess={async () => {
            setIsCreateTaskModalOpen(false);
            await loadTasks();
          }}
        />
      )}

      <CreateBookingModal
        isOpen={isCreateBookingModalOpen}
        onClose={() => {
          console.log('📱 User manually closed CreateBookingModal, clearing localStorage');
          clearModalData(); // Clear modal persistence data
          setIsCreateBookingModalOpen(false);
        }}
        onSuccess={async () => {
          console.log('📱 CreateBookingModal success, clearing localStorage');
          clearModalData(); // Clear modal persistence data
          setIsCreateBookingModalOpen(false);
          await loadTasks();
        }}
      />

      <CreateProposalModal
        isOpen={isCreateProposalModalOpen}
        onClose={() => setIsCreateProposalModalOpen(false)}
        onSuccess={async () => {
          setIsCreateProposalModalOpen(false);
          await loadTasks();
        }}
      />

      <WithdrawStockModal
        isOpen={isWithdrawStockModalOpen}
        onClose={() => setIsWithdrawStockModalOpen(false)}
        onSuccess={async () => {
          setIsWithdrawStockModalOpen(false);
          await loadTasks();
        }}
      />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
      />

      <CreateMobileBookingModal
        isOpen={isCreateMobileBookingModalOpen}
        onClose={() => {
          console.log('📱 User manually closed CreateMobileBookingModal, clearing localStorage');
          clearMobileModalData(); // Clear modal persistence data
          setIsCreateMobileBookingModalOpen(false);
        }}
        onSuccess={async () => {
          console.log('📱 CreateMobileBookingModal success, clearing localStorage');
          clearMobileModalData(); // Clear modal persistence data
          setIsCreateMobileBookingModalOpen(false);
          await loadTasks();
        }}
      />
    </div>
  );
}

function AuthRedirectHandler() {
  console.log('🔥 AuthRedirectHandler mounting');
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [isLoading, setIsLoading] = useState(true);
  const [debugInfo, setDebugInfo] = useState<string>('Initializing...');

  useEffect(() => {
    // Check if we should skip the initializing screen
    const skipInitializing = localStorage.getItem('skipInitializing') === 'true';
    const preloadedRoute = localStorage.getItem('preloadedRoute');
    
    if (skipInitializing && preloadedRoute) {
      console.log('🚀 Skipping initialization, navigating to preloaded route:', preloadedRoute);
      // Clear the flags
      localStorage.removeItem('skipInitializing');
      localStorage.removeItem('preloadedRoute');
      // Navigate immediately
      navigate(preloadedRoute, { replace: true });
      return;
    }

    // Track when the component mounted for minimum loading time
    const startTime = Date.now();
    const minLoadingTime = 2000; // Minimum 2 seconds of loading screen
    
    const handleRedirect = async () => {
      console.log('🔄 AuthRedirectHandler running');
      setDebugInfo('AuthRedirectHandler started...');
      
      try {
    // Check if there's a code parameter in the URL (from password reset email)
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const type = params.get('type');
        
        setDebugInfo(prev => `${prev}\n\nChecking URL params:\ncode: ${code || 'none'}\ntype: ${type || 'none'}`);
    
    if (code) {
      setIsLoading(false);
          console.log('✉️ Auth code detected, redirecting to password update page');
          setDebugInfo(prev => `${prev}\n\nAuth code detected - handling password reset`);
      
          // For password reset, navigate directly (no delay needed)
      if (type === 'recovery' || !type) {
          navigate(`/reset-password/update${location.search}`, { replace: true });
        return;
      }
      
      // For other auth flows, redirect appropriately
      // ...
    } else {
          // No auth code, we need to determine default route based on settings
          console.log('🔍 Determining default route from database settings');
          setDebugInfo(prev => `${prev}\n\nNo auth code - determining default home screen`);
          
          if (!selectedBusiness?.id || !supabase) {
            console.log('⚠️ No business ID or database connection');
            setDebugInfo(prev => `${prev}\n\nNo business/DB connection`);
            
            // Calculate remaining loading time to ensure minimum display time
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
      setTimeout(() => {
              navigate('/calendarV2', { replace: true }); // Default to location calendar
              setIsLoading(false);
            }, remainingTime);
            return;
          }
          
          try {
            setDebugInfo(prev => `${prev}\n\nQuerying database for business ID: ${selectedBusiness.id}`);
            
            // Try direct RPC first for better reliability 
            try {
              const { data: directData, error: directError } = await supabase.rpc(
                'get_home_screen_direct',
                { business_id_param: selectedBusiness.id }
              );
              
              console.log("Direct query result:", directData, directError);
              setDebugInfo(prev => `${prev}\n\nDirect query: ${JSON.stringify(directData || {})}`);
              
              if (!directError && directData && directData.home_screen) {
                // Calculate remaining loading time to ensure minimum display time
                const elapsedTime = Date.now() - startTime;
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
                
                setTimeout(() => {
                  if (directData.home_screen === 'mobile_calendar') {
                    console.log("Going to mobile calendar");
                    navigate('/mobile-calendar', { replace: true });
                  } else {
                    console.log("Going to location calendar");
                    navigate('/calendarV2', { replace: true });
                  }
                  setIsLoading(false);
                }, remainingTime);
                return;
              }
            } catch (error) {
              console.log("Direct query failed:", error);
              setDebugInfo(prev => `${prev}\n\nDirect query failed, trying regular query...`);
            }
            
            // Fallback to regular table query
            const { data, error } = await supabase
              .from('application_settings')
              .select('*')
              .eq('business_id', selectedBusiness.id);
              
            console.log("Regular query result:", data, error);
            setDebugInfo(prev => `${prev}\n\nRegular query: ${JSON.stringify(data || [])}`);
            
            // Calculate remaining loading time to ensure minimum display time
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            setTimeout(() => {
              if (error || !data || data.length === 0) {
                console.log("No settings found or error, using default");
                navigate('/calendarV2', { replace: true }); // Default to location calendar
              } else {
                const setting = data[0];
                console.log(`Found setting: ${JSON.stringify(setting)}`);
                
                if (setting.home_screen === 'mobile_calendar') {
                  console.log("Going to mobile calendar");
                  navigate('/mobile-calendar', { replace: true });
                } else {
                  console.log("Going to location calendar");
                  navigate('/calendarV2', { replace: true });
                }
              }
              setIsLoading(false);
            }, remainingTime);
          } catch (error: any) {
            console.error('Error during database query:', error);
            setDebugInfo(prev => `${prev}\n\nError: ${error?.message || JSON.stringify(error)}`);
            
            // Calculate remaining loading time to ensure minimum display time
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            setTimeout(() => {
              navigate('/calendarV2', { replace: true }); // Default to location calendar
              setIsLoading(false);
            }, remainingTime);
          }
        }
      } catch (error: any) {
        console.error('Error in AuthRedirectHandler:', error);
        setDebugInfo(prev => `${prev}\n\nGlobal error: ${error?.message || JSON.stringify(error)}`);
        
        // Calculate remaining loading time to ensure minimum display time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          navigate('/calendarV2', { replace: true }); // Default to location calendar
          setIsLoading(false);
        }, remainingTime);
      }
    };
    
    handleRedirect();
  }, [location, navigate, selectedBusiness?.id]);
  
  // Always show loading screen until we're ready to navigate
  // The actual navigation happens through the navigate() calls in the useEffect
  return (
    <div className="min-h-screen bg-[#f6f6f7] flex items-center justify-center">
      <div className="text-center w-full max-w-xl p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-6"></div>
        <p className="mt-4 mb-4 text-gray-600">Initializing GitBooked</p>
      </div>
    </div>
  );
};

// Custom DefaultRoute component that redirects based on application settings
const DefaultRoute = () => {
  console.log('🔥 DefaultRoute component mounting');
  const { selectedBusiness } = useBusiness();
  const [route, setRoute] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('Initializing...');
  const [isLoading, setIsLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  
  useEffect(() => {
    // Check if we should skip the initializing screen
    const skipInitializing = localStorage.getItem('skipInitializing') === 'true';
    const preloadedRoute = localStorage.getItem('preloadedRoute');
    
    if (skipInitializing && preloadedRoute) {
      console.log('🚀 DefaultRoute: Skipping initialization, using preloaded route:', preloadedRoute);
      // Clear the flags
      localStorage.removeItem('skipInitializing');
      localStorage.removeItem('preloadedRoute');
      // Set route immediately
      setRoute(preloadedRoute);
      setIsLoading(false);
      return;
    }

    // Track when the component mounted for minimum loading time
    const startTime = Date.now();
    const minLoadingTime = 2000; // Minimum 2 seconds of loading screen
    
    const directDatabaseQuery = async () => {
      try {
        console.log("🔄 Direct database query for home screen");
        setDebugInfo('Starting database query...');
        
        if (!selectedBusiness?.id || !supabase) {
          console.log("⚠️ No business ID or database connection");
          setDebugInfo(prev => `${prev}\n\nNo business/DB connection`);
          
          // Wait for minimum loading time before navigating
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
          
          setTimeout(() => {
            setRoute('/calendarV2'); // Default to location calendar
            setIsLoading(false);
          }, remainingTime);
          return;
        }
        
        console.log(`🔍 Querying settings for business ${selectedBusiness.id}`);
        setDebugInfo(prev => `${prev}\n\nQuerying for business: ${selectedBusiness.id}`);
        
        // Try direct RPC first for better reliability
        try {
          const { data: directData, error: directError } = await supabase.rpc(
            'get_home_screen_direct',
            { business_id_param: selectedBusiness.id }
          );
          
          console.log("Direct query result:", directData, directError);
          setDebugInfo(prev => `${prev}\n\nDirect query: ${JSON.stringify(directData || {})}`);
          
          if (!directError && directData && directData.home_screen) {
            // Calculate remaining loading time to ensure minimum display time
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
            
            setTimeout(() => {
              if (directData.home_screen === 'mobile_calendar') {
                console.log("Going to mobile calendar");
                setRoute('/mobile-calendar');
              } else {
                console.log("Going to location calendar");
                setRoute('/calendarV2');
              }
              setIsLoading(false);
            }, remainingTime);
            return;
          }
        } catch (error) {
          console.log("Direct query failed:", error);
          setDebugInfo(prev => `${prev}\n\nDirect query failed, trying regular query...`);
        }
        
        // Fallback to regular table query
        const { data, error } = await supabase
          .from('application_settings')
          .select('*')
          .eq('business_id', selectedBusiness.id);
          
        console.log("Regular query result:", data, error);
        setDebugInfo(prev => `${prev}\n\nRegular query: ${JSON.stringify(data || [])}`);
        
        // Calculate remaining loading time to ensure minimum display time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          if (error || !data || data.length === 0) {
            console.log("No settings found or error, using default");
            setRoute('/calendarV2'); // Default to location calendar
          } else {
            const setting = data[0];
            console.log(`Found setting: ${JSON.stringify(setting)}`);
            
            if (setting.home_screen === 'mobile_calendar') {
              console.log("Going to mobile calendar");
              setRoute('/mobile-calendar');
            } else {
              console.log("Going to location calendar");
              setRoute('/calendarV2');
            }
          }
          setIsLoading(false);
        }, remainingTime);
      } catch (error: any) {
        console.error("Error during database query:", error);
        setDebugInfo(prev => `${prev}\n\nError: ${error?.message || JSON.stringify(error)}`);
        
        // Wait for minimum loading time even on error
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
        
        setTimeout(() => {
          setRoute('/calendarV2'); // Default to location calendar
          setIsLoading(false);
        }, remainingTime);
      }
    };
    
    directDatabaseQuery();
  }, [selectedBusiness?.id]);
  
  // Always show loading screen until we're ready to navigate
  if (isLoading || !route) {
    return (
      <div className="min-h-screen bg-[#f6f6f7] flex items-center justify-center">
        <div className="text-center w-full max-w-xl p-6">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-6"></div>
          <p className="mt-4 mb-4 text-gray-600">
            {redirecting ? 'Preparing password reset page...' : 'Initializing GitBooked'}
          </p>
        </div>
      </div>
    );
  }
  
  // Only navigate once we're fully loaded and know our destination
  return <Navigate to={route} replace />;
};

// Add route navigation helper to prevent hard refresh issues
function useRoutePreloader() {
  useEffect(() => {
    // Preload feedback route to prevent loading issues
    const preloadRoute = () => {
      // Force React Router to recognize all routes
      if (typeof window !== 'undefined') {
        // Listen for popstate events (back/forward button)
        const handlePopState = (e: PopStateEvent) => {
          // Force a re-render if navigating to feedback routes
          if (window.location.pathname.startsWith('/feedback/')) {
            // Small delay to ensure React Router processes the route
            setTimeout(() => {
              if (window.location.pathname.startsWith('/feedback/')) {
                // The route should be handled by React Router
                console.log('Feedback route accessed:', window.location.pathname);
              }
            }, 50);
          }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
      }
    };

    preloadRoute();
  }, []);
}

const App = () => {
  // Call our hook to enable auto-selection for all number inputs
  useSelectInputOnFocus();
  
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch("/version.json", { cache: "no-store" });
        const data = await response.json();
        const currentVersion = localStorage.getItem("app-version");

        if (currentVersion && currentVersion !== data.version) {
          // New version detected, clear cache and reload
          if ("caches" in window) {
            caches.keys().then((names) => {
              names.forEach((name) => {
                caches.delete(name);
              });
            });
          }
          localStorage.setItem("app-version", data.version);
          window.location.reload();
        } else if (!currentVersion) {
          localStorage.setItem("app-version", data.version);
        }
      } catch (error) {
        console.error("Version check failed:", error);
      }
    };

    checkVersion();
    const interval = setInterval(checkVersion, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  // Run migrations when app loads
  useEffect(() => {
    const runMigrations = async () => {
      try {
        // Ensure the user_preferences table exists
        await runUserPreferencesMigration();
        
        // Migrate any existing localStorage preferences to the database
        await migrateLocalStorageToDatabase();
      } catch (error) {
        console.error('Error running migrations:', error);
      }
    };
    
    runMigrations().catch(error => {
      console.error('Failed to run migrations:', error);
    });
  }, []);

  // Initialize the reminder processor
  useReminderProcessor();

  // First, let's check if there's a toast container div in the document
  if (!document.getElementById('toast-container')) {
    const container = document.createElement('div');
    container.id = 'toast-container';
    document.body.appendChild(container);
  }

  // Add route navigation helper to prevent hard refresh issues
  useRoutePreloader();

  return (
    <>
    <SupabaseProvider>
      <UserProvider>
        <BusinessProvider business={null}>
            <ApplicationSettingsProvider>
              <PermissionProvider>
                <BookingStatus.Provider>
                <NotificationsProvider>
                  <TasksProvider>
                    <UnreadTasksProvider>
                      <UnreadMessagesProvider>
                        <TwilioMessagePollerProvider pollingInterval={5000}>
                          <CallProvider>
                            <BrowserRouter>
                              <ToastProvider>
                                <Routes>
                                  <Route path="/login" element={<Login />} />
                                  <Route path="/signup" element={<Signup />} />
                                  <Route path="/reset-password" element={<ResetPassword />} />
                                  <Route path="/reset-password/update" element={<UpdatePassword />} />
                                  <Route path="/feedback/:token" element={<FeedbackPage />} />
                                  <Route path="/" element={<AuthRedirectHandler />} />
                                  <Route path="/billing" element={<Billing />} />
                                  <Route path="/billing/success" element={<BillingSuccess />} />
                                  <Route path="/terms-of-service" element={<TermsOfService />} />
                                  <Route
                                    path="/receipt/:id"
                                    element={<PublicReceipt />}
                                  />
                                  <Route
                                    path="/proposal/:token"
                                    element={<PublicProposal />}
                                  />
                                  <Route path="/:businessSlug/book" element={<BookOnline />} />
                                  <Route path="/:businessSlug/booking-success" element={<BookingSuccess />} />
                                  <Route path="/book" element={<Navigate to="/" replace />} />
                                  <Route
                                    element={
                                      <PrivateRoute>
                                          {/* Subscription check disabled to fix loading issues */}
                                          {/* <SubscriptionRequired> */}
                                          <AppContent />
                                          {/* </SubscriptionRequired> */}
                                      </PrivateRoute>
                                    }
                                  >
                                    <Route
                                      path="/"
                                        element={<DefaultRoute />}
                                    />
                                    <Route path="/bookings" element={
                                      <PermissionGuard permission="bookings.pageAccess">
                                        <Bookings />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/proposals" element={
                                      <PermissionGuard permission="proposals.pageAccess">
                                        <Proposals />
                                      </PermissionGuard>
                                    } />
                                    {/* Payments route disabled temporarily - keeping code for future use */}
                                    {/* <Route path="/payments" element={<Payments />} /> */}
                                    <Route path="/customers" element={
                                      <PermissionGuard permission="customers.pageAccess">
                                        <Customers />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/payroll" element={
                                      <PermissionGuard permission="payroll.pageAccess">
                                        <ManagePayroll />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/stock" element={<Stock />} />
                                    <Route path="/resources" element={<Resources />} />
                                    <Route path="/services" element={<Services />} />
                                    <Route path="/reports" element={<Reports />}>
                                      <Route path="sales" element={<Sales />} />
                                      <Route
                                        path="profit-loss"
                                        element={<ProfitLoss />}
                                      />
                                    </Route>
                                    <Route path="/settings/*" element={<Settings />} />
                                    <Route path="/marketing" element={<Marketing />} />
                                    <Route path="/workflows" element={<Workflows />} />
                                    <Route path="/reports" element={<Reports />} />
                                    <Route
                                      path="/sales-reports"
                                      element={<SalesReports />}
                                    >
                                      <Route path="sales" element={<Sales />} />
                                      <Route
                                        path="profit-loss"
                                        element={<ProfitLoss />}
                                      />
                                      <Route path="cash-flow" element={<CashFlow />} />
                                    </Route>
                                    <Route path="/team-reports" element={<TeamReports />} />
                                    <Route path="/tasks" element={<Tasks />} />
                                    <Route path="/timeclock" element={<Timeclock />} />
                                    <Route path="/automations" element={
                                      <PermissionGuard permission="automations.pageAccess">
                                        <Automations />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/automations-v2" element={
                                      <PermissionGuard permission="automations.pageAccess">
                                        <AutomationsV2 />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/gps" element={<GPS />} />
                                    <Route path="/online-bookings" element={<Navigate to="/settings" state={{ from: '/online-bookings' }} replace />} />

                                    <Route path="/messages" element={
                                      <PermissionGuard permission="messages.pageAccess">
                                        <Messages />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/calendarV2" element={
                                      <PermissionGuard permission="calendar.pageAccess">
                                        <CalendarV2 />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/mobile-calendar" element={
                                      <PermissionGuard permission="calendar.pageAccess">
                                        <MobileCalendar />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/supplies-usage" element={<SuppliesUsage />} />
                                    <Route path="/crm" element={
                                      <PermissionGuard permission="crm.pageAccess">
                                        <CRM />
                                      </PermissionGuard>
                                    } />
                                    <Route path="/profile" element={<Profile />} />
                                    <Route path="/test-notification" element={<TestNotification />} />
                                    <Route path="/dialler" element={<Dialler />} />
                                    <Route path="/kpi-dashboard" element={
                                      <PermissionGuard permission="kpiDashboard.pageAccess">
                                        <KPIDashboard />
                                      </PermissionGuard>
                                    } />
                                  </Route>
                                </Routes>
                              </ToastProvider>
                            </BrowserRouter>
                          </CallProvider>
                        </TwilioMessagePollerProvider>
                      </UnreadMessagesProvider>
                    </UnreadTasksProvider>
                  </TasksProvider>
                </NotificationsProvider>
            </BookingStatus.Provider>
              </PermissionProvider>
            </ApplicationSettingsProvider>
        </BusinessProvider>
      </UserProvider>
    </SupabaseProvider>
    <ToastContainer />
    </>
  );
};

export default App;
